import { CalendarDays } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useI18n } from '../lib/i18n';
import { PRESETS, usePeriod, type Preset } from '../lib/period';
import { Button, Input, Select } from './ui';

/**
 * The period every page reads (CR-008 §3.2): a preset, a day, a month or a range — the company's calendar.
 * A custom range is held to a year by the API; a day after today is refused there too.
 */
export function PeriodPicker() {
  const { t } = useI18n();
  const { params, setPeriod } = usePeriod();
  const mode = params.preset ? params.preset : params.period ?? 'THIS_MONTH';
  const [custom, setCustom] = useState({ date: params.date ?? '', month: params.month ?? '', from: params.from ?? '', to: params.to ?? '' });
  useEffect(() => setCustom({ date: params.date ?? '', month: params.month ?? '', from: params.from ?? '', to: params.to ?? '' }), [params.date, params.month, params.from, params.to]);

  const onMode = (value: string) => {
    if ((PRESETS as readonly string[]).includes(value)) return setPeriod({ preset: value as Preset });
    const today = new Date().toISOString().slice(0, 10);
    if (value === 'DAILY') return setPeriod({ period: 'DAILY', date: custom.date || today });
    if (value === 'MONTHLY') return setPeriod({ period: 'MONTHLY', month: custom.month || today.slice(0, 7) });
    setPeriod({ period: 'CUSTOM', from: custom.from || `${today.slice(0, 7)}-01`, to: custom.to || today });
  };

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('period.label')}>
      <CalendarDays className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
      <Select value={mode} onChange={(e) => onMode(e.target.value)} aria-label={t('period.label')} className="!w-auto">
        {PRESETS.map((p) => (
          <option key={p} value={p}>
            {t(`period.preset.${p}`)}
          </option>
        ))}
        <option value="DAILY">{t('period.type.DAILY')}…</option>
        <option value="MONTHLY">{t('period.type.MONTHLY')}…</option>
        <option value="CUSTOM">{t('period.custom')}</option>
      </Select>
      {params.period === 'DAILY' && (
        <Input type="date" dir="ltr" value={custom.date} onChange={(e) => e.target.value && setPeriod({ period: 'DAILY', date: e.target.value })} aria-label={t('common.date')} className="!w-auto" />
      )}
      {params.period === 'MONTHLY' && (
        <Input type="month" dir="ltr" value={custom.month} onChange={(e) => e.target.value && setPeriod({ period: 'MONTHLY', month: e.target.value })} aria-label={t('period.type.MONTHLY')} className="!w-auto" />
      )}
      {params.period === 'CUSTOM' && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (custom.from && custom.to) setPeriod({ period: 'CUSTOM', from: custom.from, to: custom.to });
          }}
        >
          <Input type="date" dir="ltr" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} aria-label={t('common.from')} className="!w-auto" />
          <Input type="date" dir="ltr" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} aria-label={t('common.to')} className="!w-auto" />
          <Button size="sm" type="submit">
            {t('common.apply')}
          </Button>
        </form>
      )}
    </div>
  );
}
